import { DEFAULT_VIDEO_POSTER_FORMAT } from 'components/Video/constants'
import { getSrcSetAttribute } from 'lib/assetLoader'
import { ThemeName } from 'styles/theme'

export interface VideoOptions {
  width?: number
  height?: number
  cropMode?: string // defaults to limit
  theme?: ThemeName
  startOffset?: number
  quality?: 'auto' | 'auto:best' | 'auto:good' | 'auto:eco' | 'auto:low'
}

export const cdnVideoUrl = (
  videoName: string,
  {
    height,
    width,
    theme,
    startOffset,
    cropMode = 'limit',
    quality,
  }: VideoOptions = {}
) =>
  [
    'https://assets.okapa.com/video/upload',
    quality ? `q_${quality}` : undefined,
    startOffset ? `so_${startOffset}` : undefined,
    [
      width || height ? `c_${cropMode}` : undefined,
      height ? `h_${height}` : undefined,
      width ? `w_${width}` : undefined,
    ]
      .filter(Boolean)
      .join(','),
    theme,
    videoName,
  ]
    .filter(Boolean)
    .join('/')

interface VideoAndPosterOptions extends VideoOptions {
  posterStartOffset?: number
}

export const cdnVideoAndPosterUrls = (
  publicId: string,
  { posterStartOffset, ...videoOptions }: VideoAndPosterOptions = {}
) => {
  const videoUrl = cdnVideoUrl(publicId, videoOptions)

  const posterUrl = cdnVideoUrl(`${publicId}.${DEFAULT_VIDEO_POSTER_FORMAT}`, {
    ...videoOptions,
    startOffset: posterStartOffset,
  })

  return { videoUrl, posterUrl }
}

interface ImageOptions {
  theme?: string
  imageName: string
  width?: number
  height?: number
  angle?: number
  dpr?: number
  cropMode?: string
  gravity?: string
  quality?: 'auto' | 'auto:best' | 'auto:good' | 'auto:eco' | 'auto:low'
}

export const cdnImageUrl = ({
  theme,
  imageName,
  width,
  height,
  angle,
  dpr,
  cropMode = 'limit',
  gravity,
  quality,
}: ImageOptions) => {
  return [
    [
      `https://assets.okapa.com/image/upload/c_${cropMode}`,
      gravity ? `g_${gravity}` : undefined,
      height ? `h_${height}` : undefined,
      width ? `w_${width}` : undefined,
      angle ? `a_${angle}` : undefined,
      dpr ? `dpr_${dpr}` : undefined,
      quality ? `q_${quality}` : undefined,
    ]
      .filter(Boolean)
      .join(','),
    theme,
    `${imageName}.webp`,
  ]
    .filter(Boolean)
    .join('/')
}

export const cdnImageSrcSet = (options: ImageOptions) => ({
  1: cdnImageUrl(options),
  2: cdnImageUrl({ ...options, dpr: 2.0 }),
})

export const cdnImageProps = (options: ImageOptions) => ({
  src: cdnImageUrl(options),
  srcSet: getSrcSetAttribute(cdnImageSrcSet(options)),
})
